import { Section } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import type FeaturesService from '../../../services/features.ts';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { Store } from '@warp-drive/core';
import { checkEmployeeRepaymentStatus } from '../../../utils/tas-application-repayment-check.ts';
import AlertBanner from 'tio-ui/components/alert-banner';

interface RepaymentWarningBannerSignature {
  Args: {
    model: TasApplicationModel;
  };
}

/**
 * Warning banner shown at the top of the approval page when the employee has
 * OTHER applications with outstanding repayment obligations. Renders nothing
 * when there are none — safe to invoke unconditionally.
 */
export default class RepaymentWarningBanner extends Component<RepaymentWarningBannerSignature> {
  @service declare store: Store;
  @service declare features: FeaturesService;

  // Repayment check - runs on component load
  repaymentCheckData = trackedFunction(this, async () => {
    const employeeId = this.args.model.tasProgramInstance?.employee?.id;
    const companyId = this.args.model.tasProgramInstance?.employee?.company?.id;
    if (!employeeId || !companyId) {
      return null;
    }
    return checkEmployeeRepaymentStatus(this.store, employeeId, companyId);
  });

  get showRepaymentWarning() {
    // Only show warning if OTHER applications are in repayment (exclude current app)
    return this.repaymentApplicationIds.length > 0;
  }

  get repaymentApplicationIds() {
    const allApps = this.repaymentCheckData.value?.applications ?? [];
    const currentAppId = String(this.args.model.id);
    // Filter out the current application from the repayment warning list
    return allApps.filter((app) => String(app.applicationId) !== currentAppId);
  }

  /**
   * Get the correct approval route name based on consuming app
   * tio-admin: authenticated.tas.applications.approval
   * tio-employee: authenticated.admin.tas.applications.approval
   */
  get repaymentApplicationApprovalRoute() {
    return this.features.isAdminApp
      ? 'authenticated.tas.applications.approval'
      : 'authenticated.admin.tas.applications.approval';
  }

  <template>
    {{#if this.showRepaymentWarning}}
      <Section>
        <:body>
          <div class="max-w-3xl">
            <AlertBanner @intent="warning">
              <div class="tio-col-flush">
                <p class="font-medium">
                  Employee has outstanding repayment obligations for tuition assistance funds
                </p>
                <div class="tio-row-tight">
                  <span class="font-medium shrink-0">
                    Application IDs:
                  </span>
                  <div class="tio-wrap-tight">
                    {{#each this.repaymentApplicationIds as |app|}}
                      <LinkTo
                        class="underline tio-fg-warning-h tio-text-sm-medium"
                        @route={{this.repaymentApplicationApprovalRoute}}
                        @model={{app.applicationId}}
                      >
                        {{app.applicationId}}
                      </LinkTo>
                    {{/each}}
                  </div>
                </div>
              </div>
            </AlertBanner>
          </div>
        </:body>
      </Section>
    {{/if}}
  </template>
}
